import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, Text, View, StyleSheet, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { MEALS, dayFullKey, slotKey } from '../constants';
import { addMealLabelKey } from './addMealLabel';
import { buildMealUnits, slotOf, type MealUnit } from './mealUnits';
import { MealRow, type MealRowDayTag } from './MealRow';
import { SecondaryMealRow } from './SecondaryMealRow';
import { EntryRow } from './EntryRow';
import { PendingDinnerRow, RiseIn } from './PendingDinnerRow';
import { arrivalIndex, arrivalPhase, type PlanArrival } from './planArrival';
import { canAddDish, isAccepted, splitSlotDishes } from '../slotMeals';
import { nowAnchor } from '../nextMeal';
import type { PlannerOpening } from '../plannerOpening';
import { isHeroOnlySlot } from '../planHorizon';
import { addDays } from '../../../../utils/localDate';
import { entryTitle, entrySub, isEntryMeal, isSideableEntryMeal } from '../../../../redux/mealEntry';
import { proposalsOf, slotHasAcceptedMeal } from '../../proposalState';
import type { PlannedMeal } from '../../../../redux/mealPlanSlice';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { formatDayMonth, formatWeekdayShort } from '../../../../utils/dateLocale';

/** One Monday week of the scroll. Slot keys inside it are relative to its Monday. */
export interface DayListSection {
  weekStart: string;
  /** Every meal planned in a slot, primary dish first. */
  slotMeals: Map<number, PlannedMeal[]>;
}

interface Props {
  /**
   * The weeks the one scroll stacks (#722), first one on the page. See
   * `buildWeekSections`. Every callback below that takes a slot key also gets
   * the Monday it is relative to — a slot key alone cannot say which week.
   */
  sections: DayListSection[];
  recipeIndex: Record<string, RecipeIndexEntry>;
  /** Opens the recipe detail sheet for one specific dish. */
  onDetailPress: (meal: PlannedMeal) => void;
  /** Swaps one specific dish for another. */
  onReplacePress: (sk: number, meal: PlannedMeal, weekStart: string) => void;
  /** Moves one specific dish to another day or meal. */
  onMovePress: (sk: number, meal: PlannedMeal, weekStart: string) => void;
  /** Stacks an additional dish onto an already-filled slot. */
  onAddDishPress: (sk: number, weekStart: string) => void;
  /** Opens the accompaniment list for a slot that already holds a dish (#746). */
  onAddSidePress: (sk: number, weekStart: string) => void;
  /** "+" on an empty slot. */
  onAddPress: (sk: number, weekStart: string) => void;
  onEntryPress: (sk: number, weekStart: string) => void;
  onPortionsPress: (meal: PlannedMeal) => void;
  /** Removes a specific meal from the slot (used for proposals). */
  onRemoveExtra?: (meal: PlannedMeal) => void;
  /**
   * Meal indexes (0 breakfast, 1 lunch, 2 dinner) the library has no recipe left
   * for (#528). Their empty rows say so instead of showing a bare "+", so an
   * empty déjeuner reads as "you have no breakfast recipe", not as a bug.
   */
  exhaustedMealTypes?: Set<number>;
  /**
   * The dish the hero card above the list is showing. Its card is left out of
   * the list when it is the slot's only dish — it used to be drawn twice, one
   * on top of the other (#722).
   */
  heroMealId?: string | null;
  /**
   * Where the list opens (#828, `plannerOpening`): the lead, a day, or (null)
   * the top. The days already over are drawn above today, so the list opens
   * past them and the past is a scroll up.
   */
  opening?: PlannerOpening | null;
  /**
   * Drawn right above today, inside the scroll: the hero, or what is said
   * about tonight. At the top when today is not in the list. Never pinned
   * above the list — `weeklyMobileChrome.source.test.ts` forbids fixed chrome
   * there, because on a phone every fixed pixel is paid for by the recipe it
   * hides.
   */
  lead?: React.ReactNode;
  /**
   * Drawn right above the day `at` — "plan the next 7 days" (#769). The list
   * no longer ends where the plan does, so the card sits where the plan does.
   * A day outside every section falls back to the footer.
   */
  inline?: { at: string; node: React.ReactNode } | null;
  /**
   * Called when the scroll gets within a screen of its end: the caller adds a
   * week (#769). Once per length of `sections`, so a burst of scroll events
   * adds one week, not ten.
   */
  onNearEnd?: () => void;
  /** Rendered after the last day, inside the scroll. */
  footer?: React.ReactNode;
  /** The evenings « Planifier les 7 jours suivants » is filling, and how far it got (#851). */
  arrival?: PlanArrival | null;
}

export function DayList({
  sections,
  recipeIndex,
  onDetailPress,
  onReplacePress,
  onMovePress,
  onAddDishPress,
  onAddSidePress,
  onAddPress,
  onEntryPress,
  onPortionsPress,
  onRemoveExtra,
  exhaustedMealTypes,
  heroMealId,
  opening = null,
  lead,
  inline,
  onNearEnd,
  footer,
  arrival = null,
}: Props) {
  const { t } = useTranslation();
  const scrollViewRef = useRef<ScrollView>(null);
  /** Wraps everything in the scroll: what the opening is measured against. */
  const contentRef = useRef<View>(null);
  /** The view the list opens on — the lead, or the opening day's block. */
  const anchorRef = useRef<View>(null);
  /** The cook touched, wheeled or keyed the list: from then on the scroll is theirs. */
  const touchedRef = useRef(false);

  const { today } = useMemo(() => nowAnchor(), []);
  const weekStart = sections[0].weekStart;
  const anchorAt = opening ? (opening.kind === 'lead' ? 'lead' : opening.iso) : null;

  /**
   * Bring the opening to the top of the screen (#828). Runs again every time
   * the content changes size, until the cook touches the list: the plan comes
   * from Firestore after the first paint, and every past day drawn above today
   * pushes today down.
   */
  const settle = useCallback(() => {
    if (!anchorAt || touchedRef.current) return;
    const target = anchorRef.current;
    const content = contentRef.current;
    if (!target || !content) return;
    target.measureLayout(
      content as unknown as Parameters<View['measureLayout']>[0],
      (_x, y) => {
        if (touchedRef.current) return;
        scrollViewRef.current?.scrollTo({ y: Math.max(0, y - 8), animated: false });
      },
      () => {},
    );
  }, [anchorAt]);

  // A new page is a new opening.
  useEffect(() => {
    touchedRef.current = false;
  }, [weekStart]);

  useEffect(() => {
    if (!anchorAt) {
      // A page of history opens at its top, not at the offset of the last page.
      if (!touchedRef.current) scrollViewRef.current?.scrollTo({ y: 0, animated: false });
      return;
    }
    // The first layouts land over a few frames; onContentSizeChange covers the rest.
    const timers = [0, 100, 300].map((ms) => setTimeout(settle, ms));
    return () => timers.forEach(clearTimeout);
  }, [weekStart, anchorAt, settle]);

  // On the web a wheel, a trackpad or a key scrolls without a drag event.
  useEffect(() => {
    const scroller = scrollViewRef.current as unknown as { getScrollableNode?: () => HTMLElement | null } | null;
    const node = scroller?.getScrollableNode?.();
    if (!node || typeof node.addEventListener !== 'function') return;
    const touched = () => { touchedRef.current = true; };
    const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    events.forEach((name) => node.addEventListener(name, touched, { passive: true }));
    return () => events.forEach((name) => node.removeEventListener(name, touched));
  }, []);

  // Near the end: ask for one more week, once per section count.
  const nearEndAskedRef = useRef<number | null>(null);
  const handleScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!onNearEnd || nearEndAskedRef.current === sections.length) return;
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    const remaining = contentSize.height - (contentOffset.y + layoutMeasurement.height);
    if (remaining < layoutMeasurement.height) {
      nearEndAskedRef.current = sections.length;
      onNearEnd();
    }
  }, [onNearEnd, sections.length]);

  const inlineDrawn = !!inline && sections.some(
    (section) => inline.at >= section.weekStart && inline.at <= addDays(section.weekStart, 6),
  );
  // The lead goes right above today; a page of history that never reaches today
  // draws it at the top.
  const todayDrawn = sections.some(
    (section) => today >= section.weekStart && today <= addDays(section.weekStart, 6),
  );

  return (
    <ScrollView
      ref={scrollViewRef}
      style={styles.scroll}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
      onScrollBeginDrag={() => { touchedRef.current = true; }}
      onContentSizeChange={settle}
      onScroll={onNearEnd ? handleScroll : undefined}
      scrollEventThrottle={100}
      testID="planner-scroll"
    >
      <View ref={contentRef} collapsable={false}>
      {!todayDrawn && lead}

      {sections.map((section, index) => (
        <View
          key={section.weekStart}
          testID={`week-section-${section.weekStart}`}
        >
          {/* The Monday is a label in the scroll, not a page (#722). */}
          {index > 0 && (
            <WeekDivider weekStart={section.weekStart} />
          )}
          <WeekDays
            weekStart={section.weekStart}
            slotMeals={section.slotMeals}
            inline={inlineDrawn ? inline : null}
            arrival={arrival}
            today={today}
            lead={todayDrawn ? lead : undefined}
            anchorAt={anchorAt}
            anchorRef={anchorRef}
            recipeIndex={recipeIndex}
            heroMealId={heroMealId}
            exhaustedMealTypes={exhaustedMealTypes}
            onDetailPress={onDetailPress}
            onReplacePress={(sk, meal) => onReplacePress(sk, meal, section.weekStart)}
            onMovePress={(sk, meal) => onMovePress(sk, meal, section.weekStart)}
            onAddDishPress={(sk) => onAddDishPress(sk, section.weekStart)}
            onAddSidePress={(sk) => onAddSidePress(sk, section.weekStart)}
            onAddPress={(sk) => onAddPress(sk, section.weekStart)}
            onEntryPress={(sk) => onEntryPress(sk, section.weekStart)}
            onPortionsPress={onPortionsPress}
            onRemoveExtra={onRemoveExtra}
            t={t}
          />
        </View>
      ))}

      {!inlineDrawn && inline?.node}
      {footer}
      </View>
    </ScrollView>
  );
}

function WeekDivider({ weekStart }: { weekStart: string }) {
  const { t, i18n } = useTranslation();
  return (
    <View style={styles.weekDivider} testID={`week-divider-${weekStart}`}>
      <Text style={styles.weekDividerText}>
        {t('thisWeek.weekOfDivider', { date: formatDayMonth(weekStart, i18n.language) }).toUpperCase()}
      </Text>
      <View style={styles.weekDividerRule} />
    </View>
  );
}

interface WeekDaysProps {
  weekStart: string;
  slotMeals: Map<number, PlannedMeal[]>;
  inline?: { at: string; node: React.ReactNode } | null;
  arrival: PlanArrival | null;
  today: string;
  /** Drawn right above today, when today is in this week. */
  lead?: React.ReactNode;
  /** What the list opens on: `lead`, a day, or nothing. */
  anchorAt: string | null;
  anchorRef: React.RefObject<View | null>;
  recipeIndex: Record<string, RecipeIndexEntry>;
  heroMealId?: string | null;
  exhaustedMealTypes?: Set<number>;
  onDetailPress: (meal: PlannedMeal) => void;
  onReplacePress: (sk: number, meal: PlannedMeal) => void;
  onMovePress: (sk: number, meal: PlannedMeal) => void;
  onAddDishPress: (sk: number) => void;
  onAddSidePress: (sk: number) => void;
  onAddPress: (sk: number) => void;
  onEntryPress: (sk: number) => void;
  onPortionsPress: (meal: PlannedMeal) => void;
  onRemoveExtra?: (meal: PlannedMeal) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

/** One Monday week of the scroll: the days and their slots, the past included. */
function WeekDays({
  weekStart,
  slotMeals,
  inline,
  arrival,
  today,
  lead,
  anchorAt,
  anchorRef,
  recipeIndex,
  heroMealId,
  exhaustedMealTypes,
  onDetailPress,
  onReplacePress,
  onMovePress,
  onAddDishPress,
  onAddSidePress,
  onAddPress,
  onEntryPress,
  onPortionsPress,
  onRemoveExtra,
  t,
}: WeekDaysProps) {
  const { i18n } = useTranslation();
  /**
   * Days opened up to show their empty déjeuner and dîner, and « Ajouter un
   * plat » under each dish. Folded by default: the plan is dinners, and a split
   * "+ Déjeuner | + Dîner" row plus an add-dish row on every day pushed the
   * dinners below the fold (#722). Opened from the day header's "+",
   * or from the day tag on a compact card.
   */
  const [expandedDays, setExpandedDays] = useState<Set<number>>(() => new Set());

  // A new week starts folded.
  useEffect(() => {
    setExpandedDays(new Set());
  }, [weekStart]);

  const toggleDay = useCallback((di: number) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(di)) next.delete(di);
      else next.add(di);
      return next;
    });
  }, []);

  /**
   * The days of this week that are already over.
   *
   * Only for the week the cook is standing in: a week wholly in the past is a
   * week someone browsed back to, and shows all seven days as they were.
   */
  const pastDays = useMemo(() => {
    const weekEnd = addDays(weekStart, 6);
    if (today < weekStart || today > weekEnd) return [];
    return [0, 1, 2, 3, 4, 5, 6].filter((di) => addDays(weekStart, di) < today);
  }, [weekStart, today]);

  /**
   * Is this slot showing a bare "+"? Mirrors the render branches below, in
   * their order: an entry wins, then a resolvable dish, then an unsettled
   * proposal. Anything else is an empty slot.
   */
  const isEmptySlot = useCallback((sk: number): boolean => {
    const planned = slotMeals.get(sk) ?? [];
    if (planned.some(isEntryMeal)) return false;
    if (planned.some((pm) => !!recipeIndex[pm.recipeId])) return false;
    return !(proposalsOf(planned).length > 0 && !slotHasAcceptedMeal(planned));
  }, [slotMeals, recipeIndex]);

  /** The primary dish of a slot, if it draws as a MealRow. */
  const primaryOf = (sk: number) => {
    const plannedMeals = slotMeals.get(sk) ?? [];
    if (plannedMeals.some(isEntryMeal)) return null;
    const dishes = plannedMeals
      .map((pm) => ({ meal: pm, recipe: recipeIndex[pm.recipeId] ?? null }))
      .filter((d): d is { meal: PlannedMeal; recipe: RecipeIndexEntry } => d.recipe !== null);
    // The primary card is always an accepted meal. Taking dishes[0] straight
    // off the bucket drew a proposal as a full MealRow, which reads as a
    // decision the household never made.
    return { plannedMeals, dishes, ...splitSlotDishes(dishes) };
  };

  /** The dishes under a slot's main, and the row that adds another. */
  const renderSides = (
    extras: { meal: PlannedMeal; recipe: RecipeIndexEntry }[],
    showAddRow: boolean,
    sk: number,
    dayIso: string,
    mealType: string,
  ) => {
    if (extras.length === 0 && !showAddRow) return null;
    const takesSides = mealType !== 'breakfast';
    return (
      <View style={styles.extras}>
        {extras.map((d) => (
          <SecondaryMealRow
            key={d.meal.id}
            recipe={d.recipe}
            meal={d.meal}
            servings={d.meal.servings}
            isCooked={d.meal.status === 'cooked'}
            onPress={() => onDetailPress(d.meal)}
            onRemove={onRemoveExtra ? () => onRemoveExtra(d.meal) : undefined}
          />
        ))}
        {showAddRow && (
          <Pressable style={styles.addDishRow} onPress={() => (takesSides ? onAddSidePress(sk) : onAddDishPress(sk))} testID={`add-dish-${dayIso}-${mealType}`}>
            <Feather name="plus" size={13} color={Paprika.textSecondary} />
            <Text style={styles.addDishText}>{t('thisWeek.addDish')}</Text>
          </Pressable>
        )}
      </View>
    );
  };

  const renderUnit = (unit: MealUnit, di: number, dayIso: string, expanded: boolean, dayTag?: MealRowDayTag) => {
    // Déjeuner and dîner, both empty, as one row of two tap targets.
    if (unit.kind === 'split') {
      return (
        <View key={`${unit.first.id}${unit.second.id}`} style={styles.addSplit} testID={`add-split-${di}`}>
          {[unit.first, unit.second].map((m, half) => {
            const halfSk = slotOf(m, di);
            return (
              <React.Fragment key={m.id}>
                {half === 1 && <View style={styles.addSplitDivider} />}
                <Pressable
                  style={({ pressed }) => [styles.addSplitHalf, pressed && styles.addSplitHalfPressed]}
                  onPress={() => onAddPress(halfSk)}
                  accessibilityRole="button"
                  accessibilityLabel={t(addMealLabelKey(m.mealType))}
                  testID={`add-slot-${halfSk}`}
                >
                  <Feather name="plus" size={15} color={Paprika.primary} />
                  <Text style={styles.addSplitLabel} numberOfLines={1}>{t(m.labelKey)}</Text>
                </Pressable>
              </React.Fragment>
            );
          })}
        </View>
      );
    }

    const meal = unit.meal;
    const actualMi = MEALS.findIndex((m) => m.id === meal.id);
    const sk = slotKey(actualMi, di);
    const plannedMeals = slotMeals.get(sk) ?? [];
    const entryMeal = plannedMeals.find(isEntryMeal);

    // « Planifier les 7 jours suivants » is choosing this dinner, or has
    // chosen it and it has not had its turn to come in yet (#851).
    const arriving = meal.mealType === 'dinner' ? arrivalPhase(arrival, dayIso) : null;
    if (arriving === 'pending') {
      return (
        <PendingDinnerRow
          key={meal.id}
          dayIso={dayIso}
          withDayTag={!!dayTag}
          index={arrivalIndex(arrival, dayIso)}
          animate={arrival?.animate ?? false}
        />
      );
    }

    // An accompaniment goes beside a lunch or a dinner. Breakfast keeps
    // « Ajouter un plat » in the opened day, as before.
    const takesSides = meal.mealType !== 'breakfast';
    const canAdd = canAddDish(plannedMeals);

    // Handle entry meals (dining, note, leftover, skip). Resto and « rien »
    // hold the slot alone; a note or leftovers are the main, with any sides
    // drawn under them like a recipe dinner's.
    if (entryMeal) {
      const entry = entryMeal.entry!;
      const title = entryTitle(entry, (rid) => recipeIndex[rid]?.title, t);
      const sub = entrySub(entry, t);
      const sideable = isSideableEntryMeal(entryMeal);
      const sides = plannedMeals
        .filter((pm) => !isEntryMeal(pm))
        .map((pm) => ({ meal: pm, recipe: recipeIndex[pm.recipeId] ?? null }))
        .filter((d): d is { meal: PlannedMeal; recipe: RecipeIndexEntry } => d.recipe !== null);
      const hasSides = sides.some((d) => isAccepted(d.meal));
      const showAddRow = sideable && canAdd && (takesSides ? hasSides : expanded);
      return (
        <View
          key={meal.id}
          testID={`meal-${dayIso}-${meal.mealType}`}
        >
          <EntryRow
            entry={entry}
            mealLabel={t(meal.labelKey)}
            title={title}
            sub={sub}
            onTap={() => onEntryPress(sk)}
            testID={`entry-${dayIso}-${meal.mealType}`}
            onAddSidePress={sideable && takesSides && canAdd && !hasSides ? () => onAddSidePress(sk) : undefined}
          />
          {renderSides(sides, showAddRow, sk, dayIso, meal.mealType)}
        </View>
      );
    }

    // Handle recipe meals — every dish in the slot gets drawn: the first as
    // the meal card, the rest as compact rows.
    const { dishes, primary, extras } = primaryOf(sk)!;

    if (primary) {
      const hasSides = extras.some((d) => isAccepted(d.meal));
      // With a side already listed, the way to add another sits under it —
      // whether or not the day is open.
      const showAddRow = canAdd && (takesSides ? hasSides : expanded);
      return (
        <View
          key={meal.id}
          testID={`meal-${dayIso}-${meal.mealType}`}
        >
          <RiseIn rise={arriving === 'rising' && !!arrival?.animate}>
          <MealRow
            recipe={primary.recipe}
            mealLabel={dayTag ? '' : t(meal.labelKey)}
            dayTag={dayTag}
            dishCount={dishes.filter((d) => isAccepted(d.meal)).length}
            dietaryRole={primary.meal.dietaryRole}
            servings={primary.meal.servings}
            isCooked={primary.meal.status === 'cooked'}
            mealId={primary.meal.id}
            onPhotoPress={() => onDetailPress(primary.meal)}
            onShufflePress={() => onReplacePress(sk, primary.meal)}
            onMovePress={() => onMovePress(sk, primary.meal)}
            onPortionsPress={() => onPortionsPress(primary.meal)}
            onAddSidePress={takesSides && canAdd && !hasSides ? () => onAddSidePress(sk) : undefined}
          />
          </RiseIn>
          {/* No full-width « Ajouter un plat » under every dinner: it cost the
              compact list half its density (#722). The card's own pill opens
              the accompaniments instead (#746); the row is for the slot that
              already lists one, and for breakfast in the opened day. */}
          {renderSides(extras, showAddRow, sk, dayIso, meal.mealType)}
        </View>
      );
    }

    // Check if there are proposals but no accepted meal
    const hasProposals = proposalsOf(plannedMeals).length > 0;
    const hasAccepted = slotHasAcceptedMeal(plannedMeals);

    if (hasProposals && !hasAccepted) {
      // The caption explains why there is no meal row; the proposals
      // themselves render underneath it with their own accept/dismiss
      // controls, and the slot keeps its add row so it is never a dead end.
      const proposals = proposalsOf(plannedMeals);
      const addLabelForProposals = t(addMealLabelKey(meal.mealType));
      return (
        <View
          key={meal.id}
        >
          <View style={styles.proposalsEmptySlot}>
            <Text style={styles.noneOnMenuText}>{t('household.approval.noneOnMenu')}</Text>
            <Text style={styles.toSettleText}>
              {t('household.approval.toSettle', { count: proposals.length })}
            </Text>
          </View>
          <View style={styles.extras}>
            {proposals.map((proposal) => {
              const proposalRecipe = recipeIndex[proposal.recipeId];
              if (!proposalRecipe) return null;
              return (
                <SecondaryMealRow
                  key={proposal.id}
                  recipe={proposalRecipe}
                  meal={proposal}
                  servings={proposal.servings}
                  onPress={() => onDetailPress(proposal)}
                  onRemove={onRemoveExtra ? () => onRemoveExtra(proposal) : undefined}
                />
              );
            })}
          </View>
          <Pressable
            style={({ pressed }) => [styles.addRow, pressed && styles.addRowPressed]}
            onPress={() => onAddPress(sk)}
            accessibilityRole="button"
            accessibilityLabel={addLabelForProposals}
          >
            <View style={styles.addRowPlus}>
              <Feather name="plus" size={16} color={Paprika.primary} />
            </View>
            <View style={styles.addRowText}>
              <Text style={styles.addRowLabel} numberOfLines={1}>{addLabelForProposals}</Text>
            </View>
          </Pressable>
        </View>
      );
    }

    // Handle empty slots — an explicit, tappable "add" button rather
    // than a dotted rule with a faint "+" that read as a divider (#530),
    // carrying the generator's "no recipe for this meal" reason (#540)
    // as a second line so the slot still explains itself.
    const addLabel = t(addMealLabelKey(meal.mealType));
    const exhaustedHint = exhaustedMealTypes?.has(actualMi)
      ? t('thisWeek.noRecipeForMeal', { meal: t(meal.labelKey).toLowerCase() })
      : null;
    return (
      <Pressable
        key={meal.id}
        style={({ pressed }) => [styles.addRow, pressed && styles.addRowPressed]}
        onPress={() => onAddPress(sk)}
        accessibilityRole="button"
        accessibilityLabel={exhaustedHint ? `${addLabel} — ${exhaustedHint}` : addLabel}
        testID={`add-slot-${sk}`}
      >
        <View style={styles.addRowPlus}>
          <Feather name="plus" size={16} color={Paprika.primary} />
        </View>
        <View style={styles.addRowText}>
          <Text style={styles.addRowLabel} numberOfLines={1}>{addLabel}</Text>
          {exhaustedHint ? (
            <Text style={styles.addRowHint} numberOfLines={1}>{exhaustedHint}</Text>
          ) : null}
        </View>
      </Pressable>
    );
  };

  /** One day of the week: its header and the slots left after folding. */
  const renderDay = (di: number, dayIso: string) => {
    const dayLabel = t(dayFullKey(di));
    const dateLabel = formatDayMonth(dayIso, i18n.language, 'short');
    const expanded = expandedDays.has(di);

    const mealsForDay = MEALS.filter((m) => {
      if (m.weekendOnly && di < 5) return false;
      return true;
    });

    // Fold what a dinner plan does not need: empty déjeuner and dîner
    // (unless the day was opened), and tonight's dish when the hero above
    // already shows it (#722). An empty souper always stays — it is a gap
    // in the plan, and the one place to fill it by hand. A day already over
    // shows what was on it and nothing to fill (#828).
    const isPast = pastDays.includes(di);
    const allUnits = buildMealUnits(mealsForDay, di, isEmptySlot);
    const hasFolded = !isPast && allUnits.some((u) => {
      if (u.kind === 'split') return true;
      const sk = slotOf(u.meal, di);
      if (u.meal.mealType !== 'dinner' && isEmptySlot(sk)) return true;
      return !!primaryOf(sk)?.primary && canAddDish(slotMeals.get(sk) ?? []);
    });
    const units = allUnits.filter((u) => {
      if (u.kind === 'split') return expanded && !isPast;
      const sk = slotOf(u.meal, di);
      if (isHeroOnlySlot(slotMeals.get(sk) ?? [], heroMealId)) return false;
      if (isPast && isEmptySlot(sk)) return false;
      if (u.meal.mealType !== 'dinner' && isEmptySlot(sk)) return expanded;
      return true;
    });

    // Nothing left to draw: tonight, when the hero holds it (the hero is only
    // ever today's meal, `findHeroTarget`, so no other day is lost), or a
    // day already over with nothing on it.
    if (units.length === 0) return null;

    // The everyday day — one dinner and nothing else — is one card with
    // the day inside it, not a header over a card.
    const only = units.length === 1 && units[0].kind === 'meal' && units[0].meal.mealType === 'dinner'
      ? units[0]
      : null;
    const compact = !expanded && only !== null
      && (arrivalPhase(arrival, dayIso) === 'pending' || !!primaryOf(slotOf(only.meal, di))?.primary);

    return (
      <View
        key={di}
        style={[styles.dayBlock, compact && styles.dayBlockCompact]}
        testID={`day-block-${dayIso}`}
        ref={anchorAt === dayIso ? anchorRef : undefined}
        collapsable={false}
      >
        {compact ? (
          renderUnit(only!, di, dayIso, false, {
            short: formatWeekdayShort(dayIso, i18n.language),
            num: Number(dayIso.slice(8, 10)),
            a11yLabel: `${dayLabel} ${dateLabel} — ${t('thisWeek.addOtherMeal')}`,
            onPress: () => toggleDay(di),
            testID: `day-open-${dayIso}`,
          })
        ) : (
          <>
            <View style={styles.dayHeader}>
              <Text style={styles.dayName}>{dayLabel}</Text>
              <Text style={styles.dayDate}>{dateLabel}</Text>
              {hasFolded && (
                <Pressable
                  style={({ pressed }) => [styles.dayToggle, pressed && styles.dayTogglePressed]}
                  onPress={() => toggleDay(di)}
                  accessibilityRole="button"
                  accessibilityLabel={expanded ? t('thisWeek.hideOtherMeals') : t('thisWeek.addOtherMeal')}
                  testID={`day-open-${dayIso}`}
                  hitSlop={8}
                >
                  <Feather name={expanded ? 'minus' : 'plus'} size={14} color={Paprika.primary} />
                </Pressable>
              )}
            </View>
            {units.map((unit) => renderUnit(unit, di, dayIso, expanded))}
          </>
        )}
      </View>
    );
  };

  // Every day is drawn, the past included: it sits above today and the list
  // opens past it (#828). It used to fold into a « N jours passés » row, the
  // first thing on the page.
  return (
    <View>
      {[0, 1, 2, 3, 4, 5, 6].map((di) => {
        const dayIso = addDays(weekStart, di);
        const leadNode = lead !== undefined && dayIso === today ? (
          <View
            ref={anchorAt === 'lead' ? anchorRef : undefined}
            collapsable={false}
            testID="planner-today-lead"
          >
            {lead}
          </View>
        ) : null;
        const inlineNode = inline?.at === dayIso ? inline.node : null;
        const block = renderDay(di, dayIso);
        if (!leadNode && !inlineNode) return block;
        return (
          <React.Fragment key={di}>
            {leadNode}
            {inlineNode}
            {block}
          </React.Fragment>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  content: {
    paddingBottom: 100,
  },
  weekDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: 18,
    marginTop: 18,
    marginBottom: 2,
  },
  weekDividerText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10.5,
    letterSpacing: 0.8,
    color: Paprika.textMuted,
  },
  // A tonal bar, not a 1px rule (DESIGN.md — surfaces, never borders).
  weekDividerRule: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: Paprika.surfaceLow,
  },
  dayBlock: {
    marginBottom: 4,
  },
  dayBlockCompact: {
    marginBottom: 0,
  },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 2,
  },
  dayName: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 18,
    color: Paprika.textPrimary,
  },
  dayDate: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
  },
  dayToggle: {
    marginLeft: 'auto',
    alignSelf: 'center',
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.brandTint,
  },
  dayTogglePressed: {
    backgroundColor: Paprika.brandSoft,
  },
  extras: {
    paddingHorizontal: 16,
    paddingBottom: 8,
    marginTop: -2,
    gap: 6,
  },
  addDishRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLow,
  },
  addDishText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12.5,
    color: Paprika.textSecondary,
  },
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginHorizontal: 16,
    marginVertical: 4,
    paddingHorizontal: 12,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: Paprika.brandTint,
  },
  addRowPressed: {
    backgroundColor: Paprika.brandSoft,
  },
  // Déjeuner + dîner as one row: 44px where two stacked add rows cost 112.
  addSplit: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginVertical: 4,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: Paprika.brandTint,
  },
  addSplitHalf: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingVertical: 11,
    paddingHorizontal: 8,
  },
  addSplitHalfPressed: {
    backgroundColor: Paprika.brandSoft,
  },
  // A tonal seam, not a rule: the two halves are separate targets and have to
  // read that way without a 1px line (DESIGN.md — surfaces, never borders).
  addSplitDivider: {
    width: 1,
    marginVertical: 8,
    backgroundColor: Paprika.brandSoft,
  },
  addSplitLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13.5,
    color: Paprika.primary,
  },
  addRowPlus: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.brandSoft,
  },
  // The text column flexes so the label sizes to the row, never to a fixed
  // column — that 56px cap is what wrapped "DÉJEUNER" onto two lines (#530).
  addRowText: {
    flex: 1,
  },
  addRowLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13.5,
    color: Paprika.primary,
  },
  addRowHint: {
    marginTop: 1,
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: Paprika.textMuted,
  },
  // A caption, not a box: the proposal rows render under it and already carry
  // the dashed treatment, so a second dashed rule around the text would be a
  // line doing a job spacing already does.
  proposalsEmptySlot: {
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 2,
    gap: 2,
  },
  noneOnMenuText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
  },
  toSettleText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.approvalIdea,
  },
});
